import {
  ChangeDetectionStrategy,
  Component,
  computed,
  createComponent,
  EnvironmentInjector,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MapBounds, MapPoint, SharedCommonMapComponent } from '@client-monorepo/common/map';
import { NgxBottomNavigationService } from '@digipay/ngx-bottom-navigation';
import { ItemOverview, ServiceImagesType } from '@client-monorepo/common/ui-components';
import { debounceTime, Subject } from 'rxjs';
import {
  BranchModel,
  GeoQueryPolygonResponse,
  GeoQueryResponse,
  SearchBranchesBodyModel,
  StorePaymentMethodMapper,
  StoresApiService,
} from '@client-monorepo/daily-fintech/stores';
import { ApiImageComponent, ApiImageModule } from '@digipay/ng-ui-api-image';
import { NgxBadgeModule } from '@digipay/ngx-badge';
import { BackHandlerService } from '@client-monorepo/back-handler';
import { NgxAppBarComponent } from '@digipay/ngx-app-bar';
import { DeviceDetector } from '@client-monorepo/common/utilities';
import { StoreMobileMapListOverlayComponent } from '../store-mobile-map-list-overlay/store-mobile-map-list-overlay.component';
import { StoreDesktopMapListOverlayComponent } from '../store-desktop-map-list-overlay/store-desktop-map-list-overlay.component';
import { StoreCategoryToLogoMapper } from '../../data-access/constants/store-category-mapper';
import { Coordination, LocationService } from '@client-monorepo/common/location-management';
import { OrderTypes, RestrictionTypes } from '@client-monorepo/common/network';

@Component({
  selector: 'stores-applet-store-map',
  standalone: true,
  imports: [
    CommonModule,
    SharedCommonMapComponent,
    ApiImageModule,
    NgxBadgeModule,
    NgxAppBarComponent,
    StoreMobileMapListOverlayComponent,
    StoreDesktopMapListOverlayComponent,
  ],
  templateUrl: './store-map.component.html',
  styleUrl: './store-map.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StoreMapComponent implements OnInit, OnDestroy {
  protected readonly StorePaymentMethodMapper = StorePaymentMethodMapper;

  // Injections
  private injector = inject(EnvironmentInjector);
  bottomNavigationService = inject(NgxBottomNavigationService);
  storesApiService = inject(StoresApiService);
  backHandler = inject(BackHandlerService);
  deviceDetector = inject(DeviceDetector);
  locationService = inject(LocationService);

  // Variables
  isDesktop = this.deviceDetector.isDesktop();
  selectedBranch = signal<BranchModel | null>(null);
  branchList = signal<BranchModel[]>([]);
  mapBounds = new Subject<MapBounds>();
  currentLocation = signal<Coordination | null>(null);
  points = computed<MapPoint[]>(() => {
    const branches = this.branchList();
    return branches.map((branch: BranchModel, index: number) => {
      // Create the container div that will be used as the marker's HTML
      const container = document.createElement('div');
      container.style.position = 'relative';
      container.style.overflow = 'hidden';
      container.style.backgroundImage = "url('assets/map/icon-bg.svg')";
      container.style.backgroundSize = 'cover';
      container.style.width = '41px';
      container.style.height = '41px';

      const imageWrapper = document.createElement('div');
      imageWrapper.style.position = 'absolute';
      imageWrapper.style.left = '50%';
      imageWrapper.style.top = '50%';
      imageWrapper.style.transform = 'translate(-50%, -50%)';
      imageWrapper.style.borderRadius = '100%';
      imageWrapper.style.overflow = 'hidden';

      const fallbackImg = document.createElement('img');
      fallbackImg.src = StoreCategoryToLogoMapper[String(branch.store.categories[0])]
        ? 'assets/stores/store-categories/logo/' + StoreCategoryToLogoMapper[String(branch.store.categories[0])]
        : 'assets/map/icon-store.svg';
      fallbackImg.alt = 'Store';
      fallbackImg.style.width = '24px';
      fallbackImg.style.height = '24px';
      fallbackImg.style.display = 'block';

      imageWrapper.appendChild(fallbackImg);
      container.appendChild(imageWrapper);

      return {
        id: '' + index,
        html: container,
        lat: +branch.location.latitude,
        lng: +branch.location.longitude,
      };
    });
  });
  loadingBranches = signal<boolean>(true);
  branchesToShowInCarousel = computed<{ item: ItemOverview; branch: BranchModel }[][]>(() => {
    const newBranches: { item: ItemOverview; branch: BranchModel }[] = [];
    const branches = this.branchList();

    branches.slice(0, 21).map((branch, index: number) => {
      const store = branch.store;
      const item: ItemOverview = {
        image: {
          type: ServiceImagesType.IMAGE_ID,
          name: store.logoImageId,
        },
        title: `${store.title} ${branch.title}`,
        badge:
          store?.badges?.length > 0
            ? { text: store.badges[0].content, status: store.badges[0].status, mode: store.badges[0].mode }
            : undefined,
        subTitleNormal: store.subtitle,
        subTitleBold: branch.title,
        divider: index !== branches.length - 1,
      };
      newBranches.push({ item: item, branch: branch });
    });

    // Group newStores in pairs
    const groupedBranches: { item: ItemOverview; branch: BranchModel }[][] = [];
    for (let i = 0; i < newBranches.length; i += 3) {
      groupedBranches.push(newBranches.slice(i, i + 3));
    }
    return groupedBranches;
  });
  branchesListToShow = computed<{ item: ItemOverview; branch: BranchModel }[]>(() => {
    return this.branchList().map((branch, index: number) => {
      const store = branch.store;
      const item: ItemOverview = {
        image: {
          type: ServiceImagesType.IMAGE_ID,
          name: store.logoImageId,
        },
        title: `${store.title} ${branch.title}`,
        badge:
          store.badges.length > 0
            ? { text: store.badges[0].content, status: store.badges[0].status, mode: store.badges[0].mode }
            : undefined,
        subTitleNormal: store.subtitle,
        subTitleBold: branch.title,
        divider: index !== this.branchList().length - 1,
      };
      return { item, branch };
    });
  });
  totalAvailableItems = computed(() => {
    return this.branchesToShowInCarousel().reduce((total, group) => total + group.length, 0);
  });
  userLocation = signal<Coordination | undefined>(undefined);

  ngOnInit() {
    this.getLocation();
    this.bottomNavigationService.hide();
    this.mapBounds.pipe(debounceTime(1000)).subscribe((res) => {
      this.getStores(res);
    });
  }

  getLocation(): void {
    this.locationService.getGuaranteedLocation(true).subscribe({
      next: (val) => {
        const coordination = val.coordination;
        this.userLocation.set(coordination);
        this.currentLocation.set(coordination);
      },
    });
  }

  onAreaChange($event: MapBounds) {
    this.mapBounds.next($event);
  }

  onPointSelect($event: MapPoint) {
    this.selectedBranch.set(this.branchList()[+$event.id]);
  }

  goToStoreShop(branch: BranchModel) {
    const store = branch.store;
    if (store && branch.title) {
      this.currentLocation.set(branch.location);
    }
    this.selectedBranch.set(branch);
  }

  private getStores(mapBounds: MapBounds) {
    this.loadingBranches.set(true);
    const loc = this.userLocation() ?? this.locationService.defaultLocation;
    const body: SearchBranchesBodyModel = {
      latitude: loc.latitude,
      longitude: loc.longitude,
      size: 100,
      page: 0,
      searchRequest: {
        restrictions: [
          {
            type: RestrictionTypes.SIMPLE,
            field: 'within',
            value: (this.mapBoundsToGeoRequest(mapBounds, 'polygon') as GeoQueryPolygonResponse).corners,
            operation: 'eq',
          },
        ],
        orders: [],
      },
    };

    this.storesApiService.getBranches(body).subscribe((res) => {
      this.branchList.set(res.branches);
      this.loadingBranches.set(false);
    });
  }

  private mapBoundsToGeoRequest(mapBounds: MapBounds, type: 'polygon' | 'circle'): GeoQueryResponse {
    if (type === 'polygon') {
      return {
        corners: [
          { longitude: mapBounds.west, latitude: mapBounds.south },
          { longitude: mapBounds.east, latitude: mapBounds.south },
          { longitude: mapBounds.east, latitude: mapBounds.north },
          { longitude: mapBounds.west, latitude: mapBounds.north },
        ],
      };
    }
    return {
      center: {
        longitude: mapBounds.circle.center.lng,
        latitude: mapBounds.circle.center.lat,
      },
      radius: mapBounds.circle.radius / 1000,
    };
  }

  handleBackBtn(): void {
    if (this.selectedBranch()?.title) {
      this.resetSelectedStore();
    } else {
      this.goBack();
    }
  }

  resetSelectedStore() {
    this.selectedBranch.set(null);
    this.currentLocation.set(null);
  }

  goBack() {
    this.backHandler.setCustomBackUrl('stores');
    this.backHandler.goBack();
  }

  /**
   * Not Used until we find a better way**/
  loadUiApiImageDynamically(imageId: string): void {
    // Dynamically create the UiApiImageComponent
    const componentRef = createComponent(ApiImageComponent, {
      environmentInjector: this.injector,
    });
    // Set the component
    componentRef.setInput('imageId', imageId);
    componentRef.setInput('width', '32px');
    componentRef.setInput('height', '32px');
    componentRef.setInput('showLoading', false);
    componentRef.setInput('showFall', false);
    componentRef.setInput('fallbackImage', 'assets/map/icon-store.svg');
    componentRef.changeDetectorRef.detectChanges();
  }

  ngOnDestroy(): void {
    this.bottomNavigationService.show();
  }
}
